import React, {useCallback, useMemo} from 'react';

import styled from 'styled-components';

import {RequestedDeliveryDatePicker} from 'components/customer/Job/components/RequestedDeliveryDatePicker';
import {Button} from 'react-bootstrap';
import {ErrorMessage, Formik, FormikProps, Form} from 'formik';
import {object, string} from 'yup';
import {useParams} from 'react-router-dom';
import {useUpdateRequestedDeliveryDateMutation} from 'components/customer/Job/store/jobApi';
import {useAppContext} from 'contexts/ApplicationContext';
import {useDialogContext} from 'contexts/DialogContext';
import {useJobContext} from 'contexts/JobContext';
import {DateTime} from '@cabinetsbycomputer/datetime';
import {PartialJob} from 'shared/types/PartialJob';
import {RequestedDeliveryDateFrequencyEnum as RDD} from 'components/manufacturer/Preferences/entity/Preferences';
import {Event} from 'shared/events/Event';

const validationSchema = object().shape({
    requestedDeliveryDate: string().required(
        'Please select a requested delivery date.'
    ),
});

export const useRequestedDeliveryDateHandler = (isDashboard = false): Event => {
    const {showDialog, hideDialog} = useDialogContext();
    const {job} = useJobContext() as PartialJob;
    const {userProfile} = useAppContext();
    const {jobId} = useParams();
    const minimumLeadTime = userProfile?.minimumLeadTime ?? 0;
    const allowDeliveryDateRequest = userProfile?.allowDeliveryDateRequest;
    const isDateRequired = !!userProfile?.requireDeliveryDateRequest;
    const [updateJob, {isLoading}] = useUpdateRequestedDeliveryDateMutation();

    const initialValues = {
        requestedDeliveryDate: '',
    };

    const disablePrompt = useMemo(() => {
        if (!allowDeliveryDateRequest) {
            return true;
        }

        if (isDashboard) {
            return (
                userProfile?.requestedDeliveryDateFrequency !==
                RDD.JOB_EDIT_AND_SUBMISSION
            );
        }

        return userProfile?.requestedDeliveryDateFrequency === RDD.NEVER;
    }, [userProfile, isDashboard, allowDeliveryDateRequest]);

    const requestedDeliveryDate = useMemo(
        () => job.requestedDeliveryDate,
        [job]
    );

    const isDateMissing = useMemo(
        () =>
            !isDashboard &&
            isDateRequired &&
            !!job?.id &&
            userProfile?.requestedDeliveryDateFrequency != RDD.NEVER &&
            (requestedDeliveryDate === undefined ||
                requestedDeliveryDate === '' ||
                requestedDeliveryDate === null),
        [
            isDashboard,
            isDateRequired,
            job?.id,
            requestedDeliveryDate,
            userProfile,
        ]
    );

    const isDateExpired = useMemo(() => {
        const minLeadTime = DateTime.startOf('day').add({
            days: minimumLeadTime,
        });
        if (requestedDeliveryDate) {
            const deliveryDate = DateTime.parse(requestedDeliveryDate);

            const {days} = deliveryDate.diff(minLeadTime.get(), 'days');
            if (days != undefined) {
                return days < 0;
            }
        }
    }, [minimumLeadTime, requestedDeliveryDate]);

    const submitHandler = useCallback(
        (resolve: (value: boolean) => void) =>
            ({requestedDeliveryDate}: typeof initialValues) => {
                void updateJob({
                    jobId: Number(jobId) - 10000,
                    requestedDeliveryDate,
                })
                    .unwrap()
                    .then(() => {
                        if (hideDialog) {
                            hideDialog();
                        }
                        resolve(true);
                    });
            },
        [hideDialog, jobId, updateJob]
    );

    const handleDateChange = useCallback(
        (setFieldValue: FormikProps<typeof initialValues>['setFieldValue']) =>
            (value: string) => {
                void setFieldValue('requestedDeliveryDate', value);
            },
        []
    );

    const action = useCallback((): Promise<boolean | void> | boolean => {
        if (disablePrompt) {
            return true;
        } else {
            if (isDateExpired || isDateMissing) {
                return new Promise((resolve) => {
                    if (showDialog) {
                        showDialog({
                            title: isDateMissing
                                ? 'Select a Requested Delivery Date'
                                : 'Select a new Requested Delivery Date',
                            message: (
                                <>
                                    <div>
                                        {isDateMissing
                                            ? 'Please select a Requested Delivery Date to continue with your order.'
                                            : 'The Requested Delivery Date for your job is no longer available, please update to continue with your order.'}
                                    </div>
                                    <Formik
                                        enableReinitialize={true}
                                        initialValues={initialValues}
                                        validationSchema={validationSchema}
                                        onSubmit={submitHandler(resolve)}>
                                        {({
                                            values,
                                            setFieldValue,
                                            touched,
                                            errors,
                                        }: FormikProps<
                                            typeof initialValues
                                        >) => {
                                            return (
                                                <Form
                                                    id="cbc-job-form"
                                                    noValidate
                                                    style={{
                                                        padding: '20px 0 5px',
                                                    }}
                                                    className="cbc-form">
                                                    <div className="form-group">
                                                        <label
                                                            style={{
                                                                fontSize:
                                                                    '0.85rem',
                                                            }}>
                                                            Requested Delivery
                                                            Date:
                                                            {isDateRequired ? (
                                                                <span>*</span>
                                                            ) : null}
                                                        </label>
                                                        <RequestedDeliveryDatePicker
                                                            value={
                                                                values.requestedDeliveryDate
                                                            }
                                                            onChange={handleDateChange(
                                                                setFieldValue
                                                            )}
                                                            minimumLeadTime={
                                                                minimumLeadTime
                                                            }
                                                            isInvalid={
                                                                !!touched.requestedDeliveryDate &&
                                                                !!errors.requestedDeliveryDate
                                                            }
                                                        />
                                                    </div>
                                                    <ErrorMessage
                                                        name="requestedDeliveryDate"
                                                        component={Error}
                                                    />
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent:
                                                                'end',
                                                            marginTop: '15px',
                                                        }}>
                                                        <SubmitButton
                                                            disabled={isLoading}
                                                            type="submit"
                                                            className="button-blue btn-link">
                                                            Submit
                                                        </SubmitButton>
                                                    </div>
                                                </Form>
                                            );
                                        }}
                                    </Formik>
                                </>
                            ),
                            hideYesButton: true,
                            hideNoButton: true,
                            hideFooter: true,
                            onHide: () => resolve(false),
                        });
                    }
                });
            } else {
                return true;
            }
        }
    }, [isDateExpired, disablePrompt, isDateMissing, isDateRequired]);

    return {
        action,
    };
};

const Error = styled.div`
    color: red;
`;

const SubmitButton = styled(Button)`
    font-size: 0.85rem;
    width: 85px;
    padding: 6px 12px;
    justify-content: center;
`;
